import React, { useEffect } from 'react';
import { Info } from 'lucide-react';
import Badge from '../UI/Badge';
import { calculateRequiredCapital } from '@/lib/calculateRequiredCapital';

const CapitalRequirements = ({
  strategyType,
  parameters,
  accountBalance = 100000, // Default paper trading balance
  buffer = 20, // Buffer percentage
  onBufferChange,
  onInvestmentCapChange,
}) => {
  const requiredCapital = calculateRequiredCapital(strategyType, parameters);
  const bufferAmount = (requiredCapital * buffer) / 100;
  const totalRequired = requiredCapital + bufferAmount;
  const isInsufficientFunds = totalRequired > accountBalance;
  const utilizationPercent = ((totalRequired / accountBalance) * 100).toFixed(1);

  useEffect(() => {
    onInvestmentCapChange?.(totalRequired > 0 ? totalRequired : null);
  }, [totalRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Account Balance */}
      <div className="bg-card rounded-lg p-4 border border-border">
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Available Balance</div>
            <div className="text-2xl font-bold text-foreground">
              ${accountBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <Badge variant="info">PAPER</Badge>
        </div>

        {/* Utilization Bar */}
        <div className="mt-3">
          <div className="flex justify-between text-xs text-muted-foreground mb-1">
            <span>Capital Utilization</span>
            <span>{utilizationPercent}%</span>
          </div>
          <div className="w-full h-2 bg-accent/10 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                isInsufficientFunds
                  ? 'bg-red-500'
                  : utilizationPercent > 80
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Capital Breakdown */}
      <div className="bg-card rounded-lg p-4 border border-border flex-1 flex flex-col">
        <h4 className="text-sm font-semibold text-foreground mb-3">Capital Requirements</h4>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Strategy Capital</span>
            <span className="text-foreground font-semibold">
              ${requiredCapital.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className="flex justify-between">
            <span className="text-muted-foreground">Buffer ({buffer}%)</span>
            <span className="text-foreground font-semibold">
              ${bufferAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className="border-t border-border pt-2 mt-2 flex justify-between">
            <span className="text-muted-foreground font-semibold">Total Required</span>
            <span className={`font-bold ${isInsufficientFunds ? 'text-red-500' : 'text-green-500'}`}>
              ${totalRequired.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Remaining Balance</span>
            <span className={accountBalance - totalRequired >= 0 ? 'text-muted-foreground' : 'text-red-400'}>
              ${(accountBalance - totalRequired).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Buffer Slider */}
        <div className="mt-4 pt-4 border-t border-border">
          <label className="flex items-center text-xs font-medium text-muted-foreground mb-2">
            Safety Buffer: {buffer}%
            <span className="relative inline-flex ml-1 group/tip">
              <span className="text-muted-foreground hover:text-foreground transition-colors cursor-help">
                <Info className="h-3.5 w-3.5" />
              </span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 px-3 py-2 text-xs text-foreground bg-popover border border-border rounded-lg shadow-lg z-50 opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-opacity duration-150">
                Buffer helps prevent margin calls and order cancellations due to price volatility
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-border" />
              </div>
            </span>
          </label>
          <input
            type="range"
            min="0"
            max="50"
            step="5"
            value={buffer}
            onChange={(e) => onBufferChange(parseInt(e.target.value))}
            className="w-full h-2 bg-accent/10 rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
          </div>
        </div>

        {/* Warning — inline within this card */}
        {isInsufficientFunds && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="bg-red-900/20 border border-red-700 text-red-200 px-3 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm">&#9888;&#65039;</span>
                <div>
                  <div className="text-sm font-bold">Insufficient Funds</div>
                  <div className="text-xs leading-snug text-red-300">
                    Requires ${totalRequired.toLocaleString()}, available ${accountBalance.toLocaleString()}.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isInsufficientFunds && utilizationPercent > 80 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="bg-yellow-900/20 border border-yellow-700 text-yellow-200 px-3 py-2 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm">&#9889;</span>
                <div>
                  <div className="text-sm font-bold">High Capital Utilization</div>
                  <div className="text-xs leading-snug text-yellow-300">
                    Using {utilizationPercent}% of available capital.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapitalRequirements;
